import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Paper, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon       from '@mui/icons-material/Add';
import EditIcon      from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  getVendors, createVendor, updateVendor, resetVendorAdminPassword,
} from '../api/client';

function extractError(err) {
  const detail = err.response?.data?.detail;
  if (!detail) return 'An error occurred';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => {
      const field = d.loc ? d.loc.slice(1).join('.') : '';
      return field ? `${field}: ${d.msg}` : d.msg;
    }).join('; ');
  }
  return JSON.stringify(detail);
}

const emptyCreateForm = {
  name: '', contact_email: '', contact_phone: '',
  admin_username: '', admin_password: '', admin_name: '', admin_email: '',
};

const emptyEditForm = {
  name: '', contact_email: '', contact_phone: '', is_active: true,
};

export default function VendorsPage() {
  const [vendors,    setVendors]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  // Dialogs
  const [createOpen,  setCreateOpen]  = useState(false);
  const [editOpen,    setEditOpen]    = useState(false);
  const [resetOpen,   setResetOpen]   = useState(false);
  const [selected,    setSelected]    = useState(null);

  const [createForm,  setCreateForm]  = useState(emptyCreateForm);
  const [editForm,    setEditForm]    = useState(emptyEditForm);
  const [newPassword, setNewPassword] = useState('');
  const [saving,      setSaving]      = useState(false);
  const [formError,   setFormError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getVendors();
      setVendors(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Create ──────────────────────────────────────────────────────────
  const handleCreateSubmit = async () => {
    setSaving(true); setFormError(null);
    try {
      await createVendor(createForm);
      setCreateOpen(false);
      setCreateForm(emptyCreateForm);
      load();
    } catch (err) {
      setFormError(extractError(err));
    } finally { setSaving(false); }
  };

  // ── Edit ────────────────────────────────────────────────────────────
  const openEdit = (v) => {
    setSelected(v);
    setEditForm({
      name:          v.name          || '',
      contact_email: v.contact_email || '',
      contact_phone: v.contact_phone || '',
      is_active:     v.is_active     !== false,
    });
    setFormError(null);
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    setSaving(true); setFormError(null);
    try {
      await updateVendor(selected.id, editForm);
      setEditOpen(false);
      load();
    } catch (err) {
      setFormError(extractError(err));
    } finally { setSaving(false); }
  };

  // ── Reset password ───────────────────────────────────────────────────
  const openReset = (v) => {
    setSelected(v);
    setNewPassword('');
    setFormError(null);
    setResetOpen(true);
  };

  const handleResetSubmit = async () => {
    setSaving(true); setFormError(null);
    try {
      await resetVendorAdminPassword(selected.id, newPassword);
      setResetOpen(false);
    } catch (err) {
      setFormError(extractError(err));
    } finally { setSaving(false); }
  };

  // ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <StorefrontIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>Vendors</Typography>
          <Chip label={vendors.length} size="small" color="primary" />
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setCreateForm(emptyCreateForm); setFormError(null); setCreateOpen(true); }}>
          New Vendor
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Vendor</strong></TableCell>
                <TableCell><strong>Contact</strong></TableCell>
                <TableCell align="center"><strong>Locations</strong></TableCell>
                <TableCell align="center"><strong>Operators</strong></TableCell>
                <TableCell><strong>Admin Username</strong></TableCell>
                <TableCell><strong>Admin Name</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No vendors yet. Create the first one.
                  </TableCell>
                </TableRow>
              )}
              {vendors.map(v => (
                <TableRow key={v.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{v.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{v.id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{v.contact_email || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{v.contact_phone || ''}</Typography>
                  </TableCell>
                  <TableCell align="center">{v.location_count ?? 0}</TableCell>
                  <TableCell align="center">{v.operator_count ?? 0}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">{v.admin_username || '—'}</Typography>
                  </TableCell>
                  <TableCell>{v.admin_name || '—'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={v.is_active ? 'Active' : 'Inactive'}
                      color={v.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit vendor">
                      <IconButton size="small" onClick={() => openEdit(v)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset admin password">
                      <IconButton size="small" onClick={() => openReset(v)}>
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ── Create Vendor Dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Vendor</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Typography variant="subtitle2" color="text.secondary" mb={1} mt={1}>Vendor Details</Typography>
          <Stack spacing={2}>
            <TextField label="Vendor Name *" value={createForm.name}
              onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
              fullWidth size="small" />
            <TextField label="Contact Email" value={createForm.contact_email}
              onChange={e => setCreateForm(f => ({ ...f, contact_email: e.target.value }))}
              fullWidth size="small" />
            <TextField label="Contact Phone" value={createForm.contact_phone}
              onChange={e => setCreateForm(f => ({ ...f, contact_phone: e.target.value }))}
              fullWidth size="small" />
          </Stack>
          <Typography variant="subtitle2" color="text.secondary" mb={1} mt={3}>Vendor Admin Account</Typography>
          <Stack spacing={2}>
            <TextField label="Admin Username *" value={createForm.admin_username}
              onChange={e => setCreateForm(f => ({ ...f, admin_username: e.target.value }))}
              fullWidth size="small" />
            <TextField label="Admin Password *" type="password" value={createForm.admin_password}
              onChange={e => setCreateForm(f => ({ ...f, admin_password: e.target.value }))}
              fullWidth size="small" />
            <TextField label="Admin Full Name *" value={createForm.admin_name}
              onChange={e => setCreateForm(f => ({ ...f, admin_name: e.target.value }))}
              fullWidth size="small" />
            <TextField label="Admin Email" value={createForm.admin_email}
              onChange={e => setCreateForm(f => ({ ...f, admin_email: e.target.value }))}
              fullWidth size="small" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Vendor Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Vendor — {selected?.name}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Stack spacing={2} mt={1}>
            <TextField label="Vendor Name *" value={editForm.name}
              onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
              fullWidth size="small" />
            <TextField label="Contact Email" value={editForm.contact_email}
              onChange={e => setEditForm(f => ({ ...f, contact_email: e.target.value }))}
              fullWidth size="small" />
            <TextField label="Contact Phone" value={editForm.contact_phone}
              onChange={e => setEditForm(f => ({ ...f, contact_phone: e.target.value }))}
              fullWidth size="small" />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Active</Typography>
              <Switch checked={editForm.is_active}
                onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))} />
              <Typography variant="caption" color={editForm.is_active ? 'success.main' : 'error.main'}>
                {editForm.is_active ? 'Enabled' : 'Disabled — admin login will be blocked'}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset Admin Password Dialog ── */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Admin Password — {selected?.name}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Typography variant="body2" mb={2} mt={1}>
            Admin username: <strong>{selected?.admin_username}</strong>
          </Typography>
          <TextField label="New Password *" type="password" value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            fullWidth size="small" autoFocus
            onKeyDown={e => e.key === 'Enter' && handleResetSubmit()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleResetSubmit} variant="contained" color="warning" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
